import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faList,
    faCirclePlay,
    faVideo,
    faCheck,
    faExclamationTriangle,
    faCertificate,
    faChevronRight,
    faChevronLeft,
    faChevronDown,
    faChevronUp,
    faInfoCircle,
    faClock,
    faChartLine,
    faDownload,
    faLock,
    faTimes,
    faPaperclip,
    faFileAlt,
    faFilePdf,
    faFileImage,
    faFileArchive,
    faFileWord,
    faFileExcel
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { getFullFileUrl } from "../../Helpers/UrlHelpers";
import ApiServices from "../Services/ApiServices";
import Loader from "../Shared/Loader";
import VideoViewerV2 from "../Shared/VideoViewerV2";
import { encryptId, decryptId, encryptData } from "../../utils/encryption";
import htmlHelper from "../../Helpers/htmlHelper";

const getFileIcon = (url = "") => {
    if (!url) return faFileAlt;
    const ext = url.split(".").pop()?.toLowerCase();
    if (["png", "jpg", "jpeg", "svg", "webp", "gif"].includes(ext)) return faFileImage;
    if (ext === "pdf") return faFilePdf;
    if (["doc", "docx"].includes(ext)) return faFileWord;
    if (["xls", "xlsx"].includes(ext)) return faFileExcel;
    if (["zip", "rar", "7z"].includes(ext)) return faFileArchive;
    return faFileAlt;
};



const getFileExt = (url = "") => {
    if (!url) return "FILE";
    return url.split(".").pop()?.toUpperCase() || "FILE";
};

const AttachmentItem = ({ att }) => {
    const [expanded, setExpanded] = useState(false);
    const hasDescription = att.description && att.title && att.description !== att.title;
    const isLongDesc = hasDescription && att.description.length > 100;

    const fileUrl = getFullFileUrl(att.attachment_url);
    const titleText = htmlHelper.stripHtml(att.title || att.description || "ملف مرفق");
    const descText = hasDescription ? htmlHelper.stripHtml(att.description) : "";
    const displayedDesc = isLongDesc && !expanded ? `${descText.slice(0, 100)}...` : descText;

    return (
        <div className="attachment-row-card">
            <div className="attachment-row-main">
                <div className="attachment-icon-box">
                    <FontAwesomeIcon icon={getFileIcon(att.attachment_url)} />
                </div>
                <div className="attachment-row-info">
                    <div className="attachment-name-wrap">
                        <span className="attachment-name">{titleText}</span>
                        <span className="attachment-ext">{getFileExt(att.attachment_url)}</span>
                    </div>
                    {hasDescription && (
                        <div className="attachment-desc-wrapper">
                            <p className="attachment-desc">{displayedDesc}</p>
                            {isLongDesc && (
                                <button
                                    type="button"
                                    className="btn-toggle-see-more"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setExpanded(!expanded);
                                    }}
                                >
                                    <span>{expanded ? "عرض أقل" : "عرض المزيد"}</span>
                                    <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="attachment-action-btn"
                    title="تحميل الملف"
                >
                    <FontAwesomeIcon icon={faDownload} />
                    <span>تحميل</span>
                </a>
            </div>
        </div>
    );
};

const CourseContent = ({ Auth = null, courseId: propCourseId } = {}) => {
    const { id: paramId } = useParams();
    const decryptedId = decryptId(paramId);
    const id = propCourseId || (decryptedId ? decryptedId : paramId);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentVideo, setCurrentVideo] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [completedVideos, setCompletedVideos] = useState(new Set());
    const [viewedVideos, setViewedVideos] = useState(new Set());
    const [progress, setProgress] = useState(0);

    const [showingQuiz, setShowingQuiz] = useState(null);
    const triggeredQuizzesRef = useRef(new Set());
    const finishingVideosRef = useRef(new Set());
    const viewedVideosRef = useRef(new Set());
    const [selectedChoice, setSelectedChoice] = useState(null);
    const [answered, setAnswered] = useState(false);
    const [isCorrect, setIsCorrect] = useState(null);

    useEffect(() => {
        triggeredQuizzesRef.current.clear();
        setShowingQuiz(null);
        setSelectedChoice(null);
        setAnswered(false);
        setIsCorrect(null);
    }, [currentVideo?.id]);

    const handleVideoTimeUpdate = (currentTime) => {
        if (showingQuiz) return;

        const roundedTime = Math.floor(currentTime);
        const questions = currentVideo?.course_video_question || [];
        const activeQuiz = questions.find(q => q.timestamp_in_seconds === roundedTime);

        if (activeQuiz && !triggeredQuizzesRef.current.has(activeQuiz.id)) {
            triggeredQuizzesRef.current.add(activeQuiz.id);
            const videoEl = document.querySelector(".video-viewer-element");
            if (videoEl) {
                videoEl.pause();
            }
            setShowingQuiz(activeQuiz);
            setSelectedChoice(null);
            setAnswered(false);
            setIsCorrect(null);
        }
    };

    const handleChoiceSelect = (choice) => {
        if (answered) return;
        setSelectedChoice(choice.id);
        setIsCorrect(choice.is_correct);
        setAnswered(true);
    };

    const handleCloseQuiz = () => {
        setShowingQuiz(null);
        setSelectedChoice(null);
        setAnswered(false);
        setIsCorrect(null);

        const videoEl = document.querySelector(".video-viewer-element");
        if (videoEl) {
            videoEl.play();
        }
    };

    const videoScrollRef = useRef(null);
    const fetchingIdRef = useRef(null);
    const urlAuthToken = searchParams.get("Auth") || searchParams.get("token");
    const shouldUseUrlAuth = Auth !== null || searchParams.has("Auth") || searchParams.has("token");
    const requestAuthToken = shouldUseUrlAuth ? (urlAuthToken || (typeof Auth === "string" ? Auth : null)) : null;

    const fetchCourseContent = useCallback(async () => {
        if (!id || fetchingIdRef.current === id) return;
        fetchingIdRef.current = id;

        try {
            setLoading(true);
            var encId = encryptId(id);
            const response = await ApiServices.get(
                `MyCourse/GetCourseContent/${encId}`,
                {},
                200000,
                requestAuthToken ? { authToken: requestAuthToken } : {}
            );
            const data = response.data;
            setCourse(data);

            if (data?.course_videos?.length > 0) {
                const watchedVideos = new Set();
                const viewedVideosSet = new Set();
                data.course_videos.forEach((video) => {
                    if (video.is_watched) {
                        watchedVideos.add(video.id);
                        viewedVideosSet.add(video.id);
                    }
                });
                setCompletedVideos(watchedVideos);
                setViewedVideos(viewedVideosSet);
                setCurrentVideo(data.course_videos[0]);
                calculateProgress(watchedVideos, data.course_videos.length);
            }
        } catch (error) {

            fetchingIdRef.current = null;
        } finally {
            setTimeout(() => setLoading(false), 800);
        }
    }, [id, requestAuthToken]);

    useEffect(() => {
        fetchCourseContent();
    }, [fetchCourseContent]);

    const calculateProgress = (completed, total) => {
        if (!total) return;
        const result = Math.round((completed.size / total) * 100);
        setProgress(result);
    };

    const updateVideoView = async (videoId) => {
        if (!videoId || viewedVideos.has(videoId) || viewedVideosRef.current.has(videoId)) {
            return;
        }
        viewedVideosRef.current.add(videoId);
        try {
            const encvideoId = encryptData(videoId);
            await ApiServices.post(
                `MyCourse/UpdateView/${encvideoId}`,
                {},
                false,
                200000,
                requestAuthToken ? { authToken: requestAuthToken } : {}
            );
            setViewedVideos(prev => new Set([...prev, videoId]));
        } catch (error) {
            viewedVideosRef.current.delete(videoId);
        }
    };

    const updateVideoFinish = async (videoId) => {
        if (!videoId || completedVideos.has(videoId) || finishingVideosRef.current.has(videoId)) {
            return;
        }
        finishingVideosRef.current.add(videoId);
        try {
            const encVidoeId = encryptData(videoId);
            await ApiServices.post(
                `MyCourse/UpdateFinish/${encVidoeId}`,
                {},
                false,
                200000,
                requestAuthToken ? { authToken: requestAuthToken } : {}
            );
            setCompletedVideos(prev => {
                const newCompleted = new Set([...prev, videoId]);
                if (course?.course_videos) {
                    calculateProgress(newCompleted, course.course_videos.length);
                    if (newCompleted.size === course.course_videos.length) {
                        setCourse(p => ({ ...p, is_finished: true }));
                    }
                }
                return newCompleted;
            });
        } catch (error) {
            finishingVideosRef.current.delete(videoId);
        }
    };

    const handleGetCertificate = () => {
        if (course && course.id) {
            const encryptedCourseId = encryptId(course.id);
            if (encryptedCourseId) {
                navigate(`/certificate/${encryptedCourseId}`);
            }
        }
    };

    const isCourseFinished = () => {
        if (!course || !course.course_videos || course.course_videos.length === 0) {
            return false;
        }
        return completedVideos.size === course.course_videos.length || course.is_finished;
    };

    if (loading) {
        return <Loader text="جاري تحضير بيئة التعلم..." />;
    }

    if (!course) {
        return (
            <div className="error-screen">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="error-card"
                >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
                    <h2>عذراً، الدورة غير متوفرة</h2>
                    <p>ربما تم نقل الدورة أو أنك لا تملك صلاحية الوصول إليها حالياً.</p>
                    <Link to="/mycourses" className="btn-back">العودة لدوراتي</Link>
                </motion.div>
                <style jsx>{`
                    .error-screen {
                        min-height: 100vh;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #0a0a0f;
                        color: #fff;
                    }
                    .error-card {
                        text-align: center;
                        background: #121218;
                        padding: 50px;
                        border-radius: 24px;
                        border: 1px solid rgba(255,255,255,0.05);
                    }
                    .error-icon { font-size: 3rem; color: #ffab00; margin-bottom: 20px; }
                    .btn-back { display: inline-block; margin-top: 20px; background: #7a6ad8; color: #fff; padding: 12px 30px; border-radius: 12px; text-decoration: none; }
                `}</style>
            </div>
        );
    }

    return (
        <div className="course-cinema-root">
            {/* Immersive Header */}
            <header className="cinema-header">
                <div className="header-left">
                    <Link to="/mycourses" className="header-back-btn">
                        <FontAwesomeIcon icon={faChevronRight} />
                    </Link>
                    <div className="course-meta-header">
                        <span className="course-name-badge">{course.name}</span>
                        <div className="progress-mini-wrapper">
                            <div className="progress-bar-mini">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progress}%` }}
                                    className="progress-fill-mini"
                                ></motion.div>
                            </div>
                            <span className="progress-text-mini">{progress}% مكتمل</span>
                        </div>
                    </div>
                </div>
                <div className="header-right">
                    <div className="instructor-badge">
                        <img
                            src={getFullFileUrl(course.course_cover)}
                            alt={course.instructor}
                            className="instructor-minitar"
                        />
                        <span>{course.instructor}</span>
                    </div>
                    {course.is_certified && isCourseFinished() && (
                        <motion.button
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            whileHover={{ scale: 1.05 }}
                            className="cert-btn-top"
                            onClick={handleGetCertificate}
                        >
                            <FontAwesomeIcon icon={faCertificate} /> الشهادة
                        </motion.button>
                    )}
                </div>
            </header>

            <div className="cinema-body">
                {/* Mobile Sidebar Backdrop Overlay */}
                <AnimatePresence>
                    {sidebarOpen && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="sidebar-backdrop"
                            onClick={() => setSidebarOpen(false)}
                        />
                    )}
                </AnimatePresence>

                {/* Immersive Main Area */}
                <main className={`cinema-main ${sidebarOpen ? "sidebar-visible" : "sidebar-hidden"}`}>
                    {!sidebarOpen && (
                        <motion.button
                            initial={{ x: 50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            className="sidebar-expand-floating-btn"
                            onClick={() => setSidebarOpen(true)}
                        >
                            <FontAwesomeIcon icon={faList} />
                            <span>محتوى الدورة</span>
                        </motion.button>
                    )}
                    <div className="video-player-container">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentVideo?.id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                                className="video-wrapper-inner"
                            >
                                {currentVideo && (
                                    <VideoViewerV2
                                        key={currentVideo.id}
                                        url={getFullFileUrl(currentVideo.video_url)}
                                        poster={getFullFileUrl(course.course_cover)}
                                        title={htmlHelper.stripHtml(currentVideo.description)}
                                        description={course.name}
                                        videoId={currentVideo.id}
                                        onPlay={() => updateVideoView(currentVideo.id)}
                                        onFinish={(videoId) => updateVideoFinish(videoId)}
                                        onTimeUpdate={handleVideoTimeUpdate}
                                        autoPlay={true}
                                        style={{ width: '100%', height: '100%', borderRadius: '12px' }}
                                    />
                                )}
                            </motion.div>
                        </AnimatePresence>

                        {/* Interactive Quiz Overlay */}
                        <AnimatePresence>
                            {showingQuiz && (
                                <motion.div
                                    className="video-quiz-overlay"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    <motion.div
                                        className="video-quiz-card"
                                        initial={{ scale: 0.95, y: 20 }}
                                        animate={{ scale: 1, y: 0 }}
                                        exit={{ scale: 0.95, y: 20 }}
                                        transition={{ type: "spring", damping: 25, stiffness: 180 }}
                                    >
                                        <div className="video-quiz-header">
                                            <span className="video-quiz-badge">سؤال تفاعلي</span>
                                            <span className="video-quiz-score">+{showingQuiz.score} نقطة</span>
                                        </div>
                                        <h2 className="video-quiz-question">{showingQuiz.question}</h2>

                                        <div className="video-quiz-choices">
                                            {showingQuiz.choices?.map((choice) => {
                                                const isChoiceSelected = selectedChoice === choice.id;
                                                let choiceClass = "";
                                                if (answered) {
                                                    if (choice.is_correct) {
                                                        choiceClass = "correct-answer";
                                                    } else if (isChoiceSelected) {
                                                        choiceClass = "selected-incorrect";
                                                    }
                                                }
                                                return (
                                                    <button
                                                        key={choice.id}
                                                        className={`video-quiz-choice ${choiceClass}`}
                                                        onClick={() => handleChoiceSelect(choice)}
                                                        disabled={answered}
                                                    >
                                                        <span>{choice.text}</span>
                                                        {answered && choice.is_correct && <FontAwesomeIcon icon={faCheck} style={{ color: "#10b981" }} />}
                                                        {answered && isChoiceSelected && !choice.is_correct && <FontAwesomeIcon icon={faTimes} style={{ color: "#ef4444" }} />}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {answered && showingQuiz.explanation && (
                                            <div className="video-quiz-explanation">
                                                <strong>التوضيح:</strong>
                                                <span>{showingQuiz.explanation}</span>
                                            </div>
                                        )}

                                        <div className="video-quiz-actions">
                                            <button className="video-quiz-btn-skip" onClick={handleCloseQuiz}>
                                                تخطي السؤال
                                            </button>
                                            {answered && (
                                                <button className="video-quiz-btn-continue" onClick={handleCloseQuiz}>
                                                    متابعة الفيديو
                                                </button>
                                            )}
                                        </div>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <div className="content-details-bar">
                        <div className="video-info-primary">
                            <h1>{htmlHelper.stripHtml(currentVideo?.description)}</h1>
                            <div className="vid-stats">
                                <span><FontAwesomeIcon icon={faClock} /> أضيفت في {currentVideo?.created_at ? currentVideo.created_at.split('T')[0].split(' ')[0] : ''}</span>
                                <span className="category-pill">{course.category}</span>
                            </div>
                        </div>

                        {/* Attachments Section for Current Video */}
                        {currentVideo?.course_video_attachments && currentVideo.course_video_attachments.length > 0 && (
                            <div className="video-attachments-section">
                                <h3 className="attachments-title">
                                    <FontAwesomeIcon icon={faPaperclip} /> مرفقات الدرس ({currentVideo.course_video_attachments.length})
                                </h3>
                                <div className="attachments-list">
                                    {currentVideo.course_video_attachments.map((att) => (
                                        <AttachmentItem key={att.id} att={att} />
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="course-description-premium">
                            <h3>نظرة عامة على الدورة</h3>
                            <p>{htmlHelper.stripHtml(course.description) || "اكتشف محتوى هذه الدورة المميز وابدأ رحلة التعلم."}</p>
                            <div className="course-perks">
                                <div className="perk-item">
                                    <FontAwesomeIcon icon={faVideo} />
                                    <span>{course.course_videos?.length || 0} مقطع تعليمي</span>
                                </div>
                                <div className="perk-item">
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                    <span>{course.material || "موارد إضافية"}</span>
                                </div>
                                {course.is_certified && (
                                    <div className="perk-item highlight">
                                        <FontAwesomeIcon icon={faCertificate} />
                                        <span>شهادة إتمام معتمدة</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Certificate Success Section */}
                        {course.is_certified && isCourseFinished() && (
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                className="certificate-success-card"
                            >
                                <div className="success-glow"></div>
                                <div className="success-content">
                                    <div className="success-icon-wrap">
                                        <FontAwesomeIcon icon={faCertificate} />
                                    </div>
                                    <div className="success-text">
                                        <h3>تهانينا يا بطل! 🎓</h3>
                                        <p>لقد أتممت متطلبات الدورة بنجاح. شهادتك المعتمدة جاهزة الآن.</p>
                                    </div>
                                    <button className="btn-get-cert" onClick={handleGetCertificate}>
                                        تحميل الشهادة الآن <FontAwesomeIcon icon={faDownload} />
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </main>

                {/* Glassmorphism Sidebar */}
                <aside className={`cinema-sidebar ${sidebarOpen ? "open" : "closed"}`}>
                    <div className="sidebar-inner">
                        <div className="sidebar-header">
                            <div className="header-title-wrap">
                                <h3><FontAwesomeIcon icon={faList} /> محتوى الدورة</h3>
                                <span className="lessons-count">{completedVideos.size} / {course.course_videos?.length || 0}</span>
                            </div>
                            <button className="sidebar-close-inner-btn" onClick={() => setSidebarOpen(false)} title="إغلاق القائمة">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="playlist-container" ref={videoScrollRef}>
                            {course.course_videos?.map((video, idx) => {
                                const isActive = currentVideo?.id === video.id;
                                const isCompleted = completedVideos.has(video.id);

                                return (
                                    <motion.div
                                        key={video.id}
                                        whileHover={{ x: -8 }}
                                        className={`playlist-item ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                                        onClick={() => {
                                            setCurrentVideo(video);
                                            if (window.innerWidth <= 1100) {
                                                setSidebarOpen(false);
                                            }
                                        }}
                                    >
                                        <div className="item-prefix">
                                            {isCompleted ? (
                                                <div className="check-box done">
                                                    <FontAwesomeIcon icon={faCheck} />
                                                </div>
                                            ) : (
                                                <div className="check-box">
                                                    {idx + 1}
                                                </div>
                                            )}
                                        </div>
                                        <div className="item-content">
                                            <div className="video-title">{htmlHelper.stripHtml(video.description)}</div>
                                            <div className="item-meta">
                                                {isActive ? (
                                                    <span className="playing-status">جاري التشغيل...</span>
                                                ) : (
                                                    <span className="duration-status"><FontAwesomeIcon icon={faVideo} /> فيديو</span>
                                                )}
                                                {video.course_video_attachments && video.course_video_attachments.length > 0 && (
                                                    <span className="attachment-badge-pill" title={`${video.course_video_attachments.length} مرفقات`}>
                                                        <FontAwesomeIcon icon={faPaperclip} /> {video.course_video_attachments.length}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {isActive && <div className="active-indicator" />}
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </aside>
            </div>

            <style jsx>{`
                .course-cinema-root {
                    background: #0a0a0f;
                    min-height: 100vh;
                    color: #fff;
                    font-family: 'Inter', 'Cairo', sans-serif;
                    overflow-x: hidden;
                    direction: rtl;
                }

                /* Header Styling */
                .cinema-header {
                    height: 72px;
                    background: rgba(18, 18, 24, 0.8);
                    backdrop-filter: blur(12px);
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0 24px;
                    position: fixed;
                    top: 0;
                    width: 100%;
                    z-index: 1000;
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                }

                .header-back-btn {
                    width: 40px;
                    height: 40px;
                    background: rgba(255,255,255,0.05);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    transition: all 0.3s;
                    text-decoration: none;
                }

                .header-back-btn:hover {
                    background: #7a6ad8;
                    transform: scale(1.05);
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    min-width: 0;
                    flex: 1;
                }

                .course-meta-header {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                    min-width: 0;
                    flex: 1;
                }

                .course-name-badge {
                    font-weight: 700;
                    font-size: 1rem;
                    color: #fff;
                    line-height: 1.4;
                    max-width: 100%;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: block;
                }

                .progress-mini-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .progress-bar-mini {
                    width: 120px;
                    height: 4px;
                    background: rgba(255,255,255,0.1);
                    border-radius: 10px;
                    overflow: hidden;
                }

                .progress-fill-mini {
                    height: 100%;
                    background: linear-gradient(90deg, #7a6ad8, #a435f0);
                }

                .progress-text-mini {
                    font-size: 0.75rem;
                    color: #8c8c94;
                    font-weight: 600;
                }

                .header-right {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                }

                .instructor-badge {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    background: rgba(255,255,255,0.05);
                    padding: 6px 14px;
                    border-radius: 50px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    border: 1px solid rgba(255,255,255,0.05);
                }

                .instructor-minitar {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    object-fit: cover;
                }

                .cert-btn-top {
                    background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
                    color: #000;
                    border: none;
                    padding: 8px 18px;
                    border-radius: 50px;
                    font-size: 0.85rem;
                    font-weight: 800;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    font-family:inherit !important;
                    gap: 6px;
                    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
                }

                /* Body Layout */
                .cinema-body {
                    display: flex;
                    padding-top: 72px;
                    min-height: 100vh;
                }

                .cinema-main {
                    flex: 1;
                    padding: 30px;
                    transition: all 0.4s ease;
                    position: relative;
                }

                .sidebar-expand-floating-btn {
                    position: fixed;
                    right: 20px;
                    top: 100px;
                    background: #7a6ad8;
                    color: #fff;
                    border: none;
                    border-radius: 50px;
                    padding: 12px 24px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-weight: 700;
                    cursor: pointer;
                    z-index: 1100;
                    box-shadow: 0 10px 30px rgba(122, 106, 216, 0.4);
                      font-family: "Zain", sans-serif;
                    transition: all 0.3s;
                }

                .sidebar-expand-floating-btn:hover {
                    background: #a435f0;
                    transform: translateY(-3px) scale(1.05);
                }

                .video-player-container {
                    width: 100%;
                    max-width: 1100px;
                    aspect-ratio: 16/9;
                    background: #000;
                    border-radius: 20px;
                    margin: 0 auto 30px;
                    position: relative;
                    box-shadow: 0 20px 50px rgba(0,0,0,0.5);
                    border: 1px solid rgba(255,255,255,0.05);
                    overflow: hidden;
                }

                .video-wrapper-inner {
                    width: 100%;
                    height: 100%;
                }

                .content-details-bar {
                    max-width: 1100px;
                    margin: 0 auto;
                }

                .video-info-primary h1 {
                    font-size: 1.8rem;
                    font-weight: 800;
                    color: #fff;
                    margin-bottom: 15px;
                }

                .vid-stats {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    color: #8c8c94;
                    font-size: 0.9rem;
                    margin-bottom: 30px;
                }

                .category-pill {
                    background: rgba(122, 106, 216, 0.15);
                    color: #7a6ad8;
                    padding: 4px 12px;
                    border-radius: 6px;
                    font-weight: 600;
                }

                .course-description-premium {
                    background: rgba(255,255,255,0.03);
                    border-radius: 24px;
                    padding: 30px;
                    border: 1px solid rgba(255,255,255,0.05);
                    margin-bottom: 40px;
                }

                .course-description-premium h3 {
                    font-size: 1.25rem;
                    font-weight: 700;
                    margin-bottom: 15px;
                    color: #7a6ad8;
                }

                .course-description-premium p {
                    color: #b1b1b8;
                    line-height: 1.8;
                    font-size: 1rem;
                    margin-bottom: 25px;
                }

                .course-perks {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 20px;
                    padding-top: 20px;
                    border-top: 1px solid rgba(255,255,255,0.05);
                }

                .perk-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-size: 0.9rem;
                    color: #8c8c94;
                    background: rgba(255,255,255,0.05);
                    padding: 8px 16px;
                    border-radius: 12px;
                }

                .perk-item.highlight {
                    background: rgba(255, 215, 0, 0.05);
                    color: #FFD700;
                    border: 1px solid rgba(255, 215, 0, 0.1);
                }

                /* Sidebar Styling */
                .cinema-sidebar {
                    width: 380px;
                    background: #121218;
                    border-left: 1px solid rgba(255,255,255,0.05);
                    position: relative;
                    transition: all 0.4s cubic-bezier(0.19, 1, 0.22, 1);
                    z-index: 100;
                }

                .cinema-sidebar.closed {
                    width: 0;
                    overflow: hidden;
                }

                .sidebar-inner {
                    width: 380px;
                    height: calc(100vh - 72px);
                    display: flex;
                    flex-direction: column;
                }

                .sidebar-header {
                    padding: 24px 30px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                }

                .header-title-wrap {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    color:white
                }

                .sidebar-header h3 {
                    font-size: 1.1rem;
                    font-weight: 700;
                    margin: 0;
                }

                .sidebar-close-inner-btn {
                    width: 32px;
                    height: 32px;
                    background: rgba(255,255,255,0.05);
                    border: none;
                    border-radius: 10px;
                    color: #fff;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .sidebar-close-inner-btn:hover {
                    background: #ff4757;
                    transform: rotate(90deg);
                }

                .lessons-count {
                    font-size: 0.85rem;
                    color: #7a6ad8;
                    font-weight: 700;
                    background: rgba(122, 106, 216, 0.1);
                    padding: 2px 10px;
                    border-radius: 50px;
                    width: fit-content;
                }

                .playlist-container {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px;
                }

                .playlist-item {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    padding: 16px;
                    border-radius: 16px;
                    cursor: pointer;
                    margin-bottom: 10px;
                    position: relative;
                    transition: all 0.3s;
                    background: rgba(255,255,255,0.02);
                }

                .playlist-item:hover {
                    background: rgba(255,255,255,0.05);
                }

                .playlist-item.active {
                    background: rgba(122, 106, 216, 0.08);
                    border: 1px solid rgba(122, 106, 216, 0.2);
                }

                .playlist-item.completed {
                    opacity: 0.85;
                }

                .item-prefix {
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .check-box {
                    width: 32px;
                    height: 32px;
                    background: rgba(255,255,255,0.05);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.85rem;
                    font-weight: 700;
                    color: #8c8c94;
                    transition: all 0.3s;
                }

                .playlist-item.active .check-box {
                    background: #7a6ad8;
                    color: #fff;
                }

                .check-box.done {
                    background: #10b981;
                    color: #fff;
                }

                .item-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 0;
                }

                .video-title {
                    font-size: 0.95rem;
                    font-weight: 600;
                    color: #fff;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    word-wrap: break-word;
                    line-height: 1.4;
                }

                .item-meta {
                    font-size: 0.8rem;
                    color: #8c8c94;
                }

                .playing-status {
                    color: #7a6ad8;
                    font-weight: 700;
                }

                .active-indicator {
                    position: absolute;
                    right: 6px;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 4px;
                    height: 24px;
                    background: #7a6ad8;
                    border-radius: 10px;
                    box-shadow: 0 0 10px #7a6ad8;
                }

                /* Certificate Card Styling */
                .certificate-success-card {
                    position: relative;
                    background: #1a1a24;
                    border-radius: 30px;
                    padding: 40px;
                    margin-top: 50px;
                    overflow: hidden;
                    border: 1px solid rgba(255, 215, 0, 0.2);
                    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
                }

                .success-glow {
                    position: absolute;
                    top: -50px;
                    right: -50px;
                    width: 150px;
                    height: 150px;
                    background: radial-gradient(circle, rgba(255, 215, 0, 0.2) 0%, transparent 70%);
                    z-index: 1;
                }

                .success-content {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    gap: 30px;
                }

                .success-icon-wrap {
                    width: 100px;
                    height: 100px;
                    background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
                    border-radius: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 3rem;
                    color: #000;
                    box-shadow: 0 10px 30px rgba(255, 215, 0, 0.3);
                }

                .success-text {
                    flex: 1;
                }

                .success-text h3 {
                    font-size: 1.8rem;
                    margin-bottom: 8px;
                    color: #fff;
                }

                .success-text p {
                    font-size: 1.1rem;
                    color: #b1b1b8;
                    margin: 0;
                }

                .btn-get-cert {
                    background: #7a6ad8;
                    color: #fff;
                    border: none;
                    padding: 18px 30px;
                    border-radius: 16px;
                    font-weight: 700;
                    font-size: 1rem;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    transition: all 0.3s;
                    font-family:inherit !important;
                    box-shadow: 0 10px 20px rgba(122, 106, 216, 0.2);
                }

                .btn-get-cert:hover {
                    transform: translateY(-3px);
                    background: #a435f0;
                    box-shadow: 0 15px 30px rgba(122, 106, 216, 0.3);
                }

                /* Scrollbar */
                .playlist-container::-webkit-scrollbar {
                    width: 5px;
                }
                .playlist-container::-webkit-scrollbar-track {
                    background: transparent;
                }
                .playlist-container::-webkit-scrollbar-thumb {
                    background: rgba(255,255,255,0.05);
                    border-radius: 10px;
                }

                .sidebar-backdrop {
                    display: none;
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.65);
                    backdrop-filter: blur(4px);
                    z-index: 1090;
                }

                @media (max-width: 1100px) {
                    .cinema-body {
                        flex-direction: column;
                    }

                    .sidebar-backdrop {
                        display: block;
                    }

                    .cinema-main {
                        padding: 20px;
                    }

                    .cinema-sidebar {
                        position: fixed;
                        top: 72px;
                        right: 0;
                        bottom: 0;
                        width: min(380px, 100vw);
                        transform: translateX(100%);
                        box-shadow: -20px 0 40px rgba(0, 0, 0, 0.4);
                        z-index: 1100;
                    }

                    .cinema-sidebar.open {
                        transform: translateX(0);
                    }

                    .sidebar-inner {
                        width: min(380px, 100vw);
                    }

                    .cinema-main { margin-right: 0 !important; }

                    .video-player-container,
                    .content-details-bar {
                        width: 100%;
                    }
                }

                @media (max-width: 768px) {
                    .cinema-header {
                        height: auto;
                        min-height: 64px;
                        padding: 10px 14px;
                        gap: 10px;
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .header-left,
                    .header-right {
                        width: 100%;
                    }

                    .header-left {
                        gap: 12px;
                        align-items: center;
                        justify-content: flex-start;
                    }

                    .course-meta-header {
                        flex: 1;
                        min-width: 0;
                    }

                    .header-right {
                        justify-content: space-between;
                        flex-wrap: wrap;
                        gap: 10px;
                    }

                    .course-name-badge {
                        font-size: 0.92rem;
                        line-height: 1.35;
                        max-width: 100%;
                        white-space: normal;
                        word-break: break-word;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }

                    .progress-mini-wrapper {
                        flex-wrap: wrap;
                        gap: 8px;
                    }

                    .progress-bar-mini {
                        width: 80px;
                    }

                    .instructor-badge {
                        padding: 4px 12px;
                        font-size: 0.82rem;
                    }

                    .cert-btn-top {
                        padding: 6px 14px;
                        font-size: 0.8rem;
                    }

                    .cinema-body {
                        padding-top: 110px;
                    }

                    .cinema-main {
                        padding: 14px;
                    }

                    .sidebar-expand-floating-btn {
                        width: auto;
                        right: 14px;
                        bottom: 16px;
                        top: auto;
                        padding: 10px 18px;
                        font-size: 0.9rem;
                    }

                    .video-player-container {
                        aspect-ratio: 16 / 9;
                        margin-bottom: 18px;
                        border-radius: 14px;
                    }

                    .video-info-primary h1 {
                        font-size: 1.3rem;
                        line-height: 1.45;
                    }

                    .vid-stats {
                        flex-direction: row;
                        flex-wrap: wrap;
                        align-items: center;
                        gap: 10px;
                        margin-bottom: 20px;
                    }

                    .course-description-premium {
                        padding: 18px;
                        border-radius: 18px;
                    }

                    .course-perks {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
                        gap: 10px;
                    }

                    .perk-item {
                        width: 100%;
                        justify-content: flex-start;
                        padding: 8px 12px;
                        font-size: 0.82rem;
                    }

                    .certificate-success-card {
                        padding: 20px;
                        margin-top: 28px;
                        border-radius: 20px;
                    }

                    .success-content {
                        flex-direction: column;
                        text-align: center;
                        gap: 16px;
                    }

                    .success-icon-wrap {
                        width: 70px;
                        height: 70px;
                        font-size: 2rem;
                        border-radius: 20px;
                    }

                    .success-text h3 {
                        font-size: 1.3rem;
                    }

                    .success-text p {
                        font-size: 0.95rem;
                    }

                    .btn-get-cert {
                        width: 100%;
                        justify-content: center;
                        padding: 14px 18px;
                    }

                    .sidebar-inner {
                        width: 100vw;
                    }

                    .sidebar-header {
                        padding: 18px 16px;
                    }

                    .playlist-container {
                        padding: 14px;
                    }

                    .playlist-item {
                        padding: 12px;
                        gap: 12px;
                    }

                    .video-title {
                        font-size: 0.9rem;
                    }

                    .sidebar-close-inner-btn {
                        width: 36px;
                        height: 36px;
                        flex: 0 0 auto;
                    }
                }

                @media (max-width: 480px) {
                    .cinema-body {
                        padding-top: 118px;
                    }

                    .course-name-badge {
                        font-size: 0.84rem;
                        line-height: 1.3;
                    }

                    .cinema-main {
                        padding: 10px;
                    }

                    .attachment-row-main {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 12px;
                    }

                    .attachment-action-btn {
                        width: 100%;
                        justify-content: center;
                    }

                    .attachment-icon-box {
                        width: 40px;
                        height: 40px;
                        font-size: 1.15rem;
                    }

                    .attachment-name {
                        font-size: 0.92rem;
                    }

                    .video-attachments-section {
                        padding: 14px;
                        border-radius: 14px;
                    }
                }

                /* ── Video Quiz Overlay ── */
                .video-quiz-overlay {
                    position: absolute;
                    inset: 0;
                    background: rgba(10, 10, 15, 0.88);
                    backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1200;
                    padding: 16px;
                }
                .video-quiz-card {
                    width: 100%;
                    max-width: 480px;
                    max-height: 90vh;
                    overflow-y: auto;
                    background: linear-gradient(135deg, #121218 0%, #1a1a24 100%);
                    border: 1px solid rgba(122, 106, 216, 0.3);
                    border-radius: 20px;
                    padding: 20px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
                    direction: rtl;
                    text-align: right;
                    position: relative;
                }

                @media (max-width: 768px) {
                    .video-quiz-overlay {
                        position: fixed;
                        z-index: 2000;
                        background: rgba(10, 10, 15, 0.94);
                    }
                    .video-quiz-card {
                        padding: 16px;
                        border-radius: 16px;
                    }
                    .video-quiz-question {
                        font-size: 1.05rem;
                    }
                }
                .video-quiz-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 16px;
                }
                .video-quiz-badge {
                    background: rgba(122, 106, 216, 0.15);
                    color: #9d8df1;
                    font-size: 0.85rem;
                    font-weight: 700;
                    padding: 4px 12px;
                    border-radius: 50px;
                    border: 1px solid rgba(122, 106, 216, 0.25);
                }
                .video-quiz-score {
                    background: rgba(16, 185, 129, 0.1);
                    color: #10b981;
                    font-size: 0.85rem;
                    font-weight: 700;
                    padding: 4px 12px;
                    border-radius: 50px;
                }
                .video-quiz-question {
                    font-size: 1.2rem;
                    font-weight: 800;
                    color: #ffffff;
                    margin: 0 0 16px 0;
                    line-height: 1.5;
                }
                .video-quiz-choices {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    margin-bottom: 20px;
                }
                .video-quiz-choice {
                    width: 100%;
                    padding: 12px 16px;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1.5px solid rgba(255, 255, 255, 0.08);
                    border-radius: 12px;
                    color: #e2e8f0;
                    font-size: 0.9rem;
                    font-weight: 600;
                    cursor: pointer;
                    text-align: right;
                    transition: all 0.25s ease;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    font-family: inherit;
                }
                .video-quiz-choice:hover:not(:disabled) {
                    background: rgba(122, 106, 216, 0.1);
                    border-color: #7a6ad8;
                    transform: translateY(-2px);
                }
                .video-quiz-choice.selected-correct {
                    background: rgba(16, 185, 129, 0.15) !important;
                    border-color: #10b981 !important;
                    color: #10b981 !important;
                }
                .video-quiz-choice.selected-incorrect {
                    background: rgba(239, 68, 68, 0.15) !important;
                    border-color: #ef4444 !important;
                    color: #ef4444 !important;
                }
                .video-quiz-choice.correct-answer {
                    background: rgba(16, 185, 129, 0.15) !important;
                    border-color: #10b981 !important;
                    color: #10b981 !important;
                }
                .video-quiz-choice:disabled {
                    cursor: not-allowed;
                    opacity: 0.85;
                }
                .video-quiz-explanation {
                    background: rgba(122, 106, 216, 0.08);
                    border: 1px solid rgba(122, 106, 216, 0.15);
                    border-radius: 12px;
                    padding: 12px 14px;
                    margin-bottom: 20px;
                    font-size: 0.85rem;
                    color: #cbd5e1;
                    line-height: 1.6;
                }
                .video-quiz-explanation strong {
                    color: #9d8df1;
                    display: block;
                    margin-bottom: 2px;
                }
                .video-quiz-actions {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 16px;
                }
                .video-quiz-btn-skip {
                    background: transparent;
                    color: #94a3b8;
                    border: none;
                    font-size: 0.9rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: color 0.2s;
                    font-family: inherit;
                }
                .video-quiz-btn-skip:hover {
                    color: #f1f5f9;
                }
                .video-quiz-btn-continue {
                    background: linear-gradient(135deg, #7a6ad8 0%, #9d8df1 100%);
                    color: #fff;
                    border: none;
                    padding: 8px 20px;
                    border-radius: 10px;
                    font-size: 0.9rem;
                    font-weight: 800;
                    cursor: pointer;
                    box-shadow: 0 4px 15px rgba(122, 106, 216, 0.3);
                    transition: all 0.25s;
                    font-family: inherit;
                }
                .video-quiz-btn-continue:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(122, 106, 216, 0.4);
                }

                /* Video Attachments Section Styling */
                .video-attachments-section {
                    margin-top: 10px;
                    margin-bottom: 32px;
                    padding: 22px;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 18px;
                    backdrop-filter: blur(12px);
                }

                .attachments-title {
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #fff;
                    margin-bottom: 18px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .attachments-title svg {
                    color: #7a6ad8;
                }

                .attachments-list {
                    display: flex;
                    flex-direction: column;
                    gap: 14px;
                    width: 100%;
                }

                .attachment-row-card {
                    background: rgba(18, 18, 24, 0.7);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 14px;
                    padding: 16px 20px;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    width: 100%;
                }

                .attachment-row-card:hover {
                    background: rgba(122, 106, 216, 0.08);
                    border-color: rgba(122, 106, 216, 0.3);
                    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
                }

                .attachment-row-main {
                    display: flex;
                    align-items: flex-start;
                    gap: 16px;
                    width: 100%;
                }

                .attachment-icon-box {
                    width: 46px;
                    height: 46px;
                    border-radius: 12px;
                    background: rgba(122, 106, 216, 0.2);
                    color: #a496f2;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.35rem;
                    flex-shrink: 0;
                    transition: all 0.3s;
                    margin-top: 2px;
                }

                .attachment-row-card:hover .attachment-icon-box {
                    background: #7a6ad8;
                    color: #fff;
                }

                .attachment-row-info {
                    flex: 1;
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .attachment-name-wrap {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }

                .attachment-name {
                    font-size: 1rem;
                    font-weight: 700;
                    color: #f1f5f9;
                    word-break: break-word;
                    line-height: 1.4;
                }

                .attachment-ext {
                    display: inline-block;
                    font-size: 0.72rem;
                    font-weight: 700;
                    color: #9d8df1;
                    background: rgba(122, 106, 216, 0.18);
                    padding: 2px 8px;
                    border-radius: 6px;
                }

                .attachment-desc-wrapper {
                    margin-top: 4px;
                }

                .attachment-desc {
                    font-size: 0.88rem;
                    color: #94a3b8;
                    line-height: 1.6;
                    margin: 0;
                    word-break: break-word;
                }

                .btn-toggle-see-more {
                    background: transparent;
                    border: none;
                    color: #7a6ad8;
                    font-size: 0.82rem;
                    font-weight: 700;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 0;
                    margin-top: 4px;
                    transition: color 0.2s;
                    font-family: inherit;
                }

                .btn-toggle-see-more:hover {
                    color: #a496f2;
                    text-decoration: underline;
                }

                .attachment-action-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 18px;
                    border-radius: 12px;
                    background: rgba(122, 106, 216, 0.15);
                    color: #fff;
                    text-decoration: none;
                    font-size: 0.88rem;
                    font-weight: 700;
                    flex-shrink: 0;
                    transition: all 0.25s ease;
                    border: 1px solid rgba(122, 106, 216, 0.3);
                }

                .attachment-action-btn:hover {
                    background: #7a6ad8;
                    color: #fff;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 15px rgba(122, 106, 216, 0.4);
                }
            `}</style>
        </div>
    );
};

export default CourseContent; 
